import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Menu, X } from "lucide-react";
import logoHeader from "images/logo-header.png";
import logoFull from "images/logo-full.png";

/*
  Shared layout pieces for the JhalaTech redesign (dark navy / warm paper theme).
  Used by every page so the header, footer, nav routing, and reveal-on-scroll
  behavior stay in sync in one place instead of being copy-pasted per page.

  Updated per JhalaTech Ecommerce Landing Page Handoff:
  - Nav: Services / What We Optimize / Process / FAQs / Contact
    (Services, What We Optimize, Process, FAQs point to homepage section
    anchors since those live on the redesigned landing page; update the
    anchor targets here if those sections move to standalone routes.)
  - Footer links, email (hello@jhalatech.in) and location (Indore, Madhya
    Pradesh, India) updated to match the handoff's footer spec.
  - Logo images (logoHeader / logoFull) left exactly as before.
*/

export const JHALA_GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap');

@keyframes floatY {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}
@keyframes marquee {
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
}
@keyframes pulseDot {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
}
@keyframes typeIn {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes wave {
  0% { transform: rotate(0deg); }
  10% { transform: rotate(14deg); }
  20% { transform: rotate(-8deg); }
  30% { transform: rotate(14deg); }
  40% { transform: rotate(-4deg); }
  50% { transform: rotate(10deg); }
  60%, 100% { transform: rotate(0deg); }
}
@keyframes blobFloat {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(20px, -18px) scale(1.06); }
}
@keyframes blobFloat2 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(-16px, 16px) scale(1.05); }
}
@keyframes pulseSoft {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(1.04); }
}

.wave-hand {
  display: inline-block;
  transform-origin: 70% 70%;
  animation: wave 2.4s ease-in-out infinite;
  animation-delay: 0.6s;
}

.nav-link {
  position: relative;
  color: #8B93A7;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  padding-bottom: 6px;
  transition: color 0.2s ease;
}
.nav-link::after {
  content: '';
  position: absolute;
  left: 0; bottom: 0;
  width: 100%; height: 1.5px;
  background: #4CE0B3;
  transform: scaleX(0);
  transform-origin: right;
  transition: transform 0.3s ease;
}
.nav-link:hover { color: #F6F4EE; }
.nav-link:hover::after { transform: scaleX(1); transform-origin: left; }

.cta-btn {
  position: relative;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.cta-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 22px -6px rgba(76, 224, 179, 0.45);
}
.logo-mark { animation: floatY 4s ease-in-out infinite; }
.star { color: #FF5C39; }

.svc-card, .value-card, .faq-item {
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}
.svc-card:hover, .value-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 14px 34px -14px rgba(10,15,30,0.22);
  border-color: #4CE0B3 !important;
}
.icon-pop { transition: transform 0.25s ease; }
.value-card:hover .icon-pop { transform: scale(1.15) rotate(-6deg); }

.work-photo-card:hover .work-card-img { transform: scale(1.07); }

.industry-marquee:hover .industry-marquee-track { animation-play-state: paused !important; }

/* Responsive utilities standing in for Tailwind's hidden/md: classes,
   which twin.macro only compiles for the tw prop, not raw className strings. */
.hidden { display: none !important; }
@media (min-width: 768px) {
  .md\\:flex { display: flex !important; }
  .md\\:hidden { display: none !important; }
  .md\\:inline-flex { display: inline-flex !important; }
}
@media (max-width: 900px) {
  .hero-grid, .why-grid, .ecom-grid, .story-grid, .ecom-services-grid,
  .other-svc-row, .contact-grid, .pricing-toggle-grid,
  .core-services-grid, .ai-grid, .audit-form-grid {
    grid-template-columns: 1fr !important;
  }
  .footer-grid { grid-template-columns: 1fr 1fr !important; }
}
@media (max-width: 640px) {
  .ecom-services-grid, .core-services-grid { grid-template-columns: 1fr !important; }
}
@media (max-width: 560px) {
  .footer-grid { grid-template-columns: 1fr !important; }
}

section[id] { scroll-margin-top: 96px; }

.legal-content { font-size: 15px; line-height: 1.75; color: #4A5468; }
.legal-content h2 { font-family: 'Space Grotesk', sans-serif; font-size: 20px; font-weight: 700; color: #0A0F1E; margin: 32px 0 12px; }
.legal-content h2:first-child { margin-top: 0; }
.legal-content p { margin: 0 0 14px; }
.legal-content ul { margin: 0 0 14px; padding-left: 20px; }
.legal-content li { margin-bottom: 8px; }
.legal-content a { color: #FF5C39; }
.legal-content strong { color: #0A0F1E; }
`;

export const NAV_LINKS = ["Services", "Work", "Process", "FAQs", "Contact"];
export const NAV_ROUTES = {
  Services: "/services",
  Work: "/work",
  Process: "/#process",
  FAQs: "/#faqs",
  Contact: "/contact-us",
};

export function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.unobserve(el); } },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

export function Reveal({ children, delay = 0 }) {
  const [ref, visible] = useReveal();
  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0)" : "translateY(16px)",
      transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
    }}>
      {children}
    </div>
  );
}

export function TagChip({ children, tone = "circuit" }) {
  const color = tone === "signal" ? "#FF5C39" : "#4CE0B3";
  return (
    <span style={{
      fontFamily: "'IBM Plex Mono', monospace", fontSize: "11px", letterSpacing: "0.02em",
      color, border: `1px solid ${color}55`, background: `${color}14`,
      padding: "3px 8px", borderRadius: "3px", display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
    }}>
      {children}
    </span>
  );
}

export function BrandLockup({ tagline = false, height = 42 }) {
  return (
    <img
      className="logo-mark"
      src={tagline ? logoFull : logoHeader}
      alt="JhalaTech — Innovate. Integrate. Elevate."
      style={{ height, width: "auto", display: "block" }}
    />
  );
}

export function TopStrip() {
  return (
    <div style={{ background: "#4CE0B3", color: "#0A0F1E", textAlign: "center", padding: "7px 16px", fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, fontWeight: 500 }}>
      Now booking Shopify Plus & website projects for Q3 2026 — limited slots
    </div>
  );
}

export function Header({ active }) {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header style={{ position: "sticky", top: 0, zIndex: 50, background: "#0A0F1Ef2", backdropFilter: "blur(8px)", borderBottom: "1px solid #23304A" }}>
      <div style={{ maxWidth: 1180, margin: "0 auto", padding: "14px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Link to="/" style={{ textDecoration: "none" }}>
          <BrandLockup />
        </Link>
        <nav style={{ display: "flex", gap: 30 }} className="hidden md:flex">
          {NAV_LINKS.map((l) => (
            <Link key={l} to={NAV_ROUTES[l]} className="nav-link" style={l === active ? { color: "#F6F4EE" } : {}}>{l}</Link>
          ))}
        </nav>
        <Link to="/contact-us" className="cta-btn md:inline-flex" style={{ display: "none", background: "#4CE0B3", color: "#0A0F1E", textDecoration: "none", border: "none", padding: "10px 20px", borderRadius: 4, fontWeight: 600, fontSize: 14, cursor: "pointer", alignItems: "center", gap: 8 }}>
          Get a Free Store Audit <ArrowUpRight size={15} />
        </Link>
        <button onClick={() => setMenuOpen(!menuOpen)} style={{ background: "none", border: "none", color: "#F6F4EE", cursor: "pointer" }} className="md:hidden">
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {menuOpen && (
        <div style={{ padding: "12px 24px 20px", display: "flex", flexDirection: "column", gap: 14, borderTop: "1px solid #23304A" }} className="md:hidden">
          {NAV_LINKS.map((l) => (
            <Link key={l} to={NAV_ROUTES[l]} onClick={() => setMenuOpen(false)} style={l === active ? { color: "#4CE0B3", fontSize: 15, textDecoration: "none" } : { color: "#F6F4EE", fontSize: 15, textDecoration: "none" }}>{l}</Link>
          ))}
        </div>
      )}
    </header>
  );
}

/* Footer link groups match the handoff's footer spec:
   Shopify Development | Ecommerce Development | Store Redesign |
   Ecommerce Migration | SEO & CRO Optimization | Custom AI Automation |
   Work | Contact | Privacy Policy | Refund Policy | Terms of Service */
const FOOTER_SERVICE_LINKS = [
  { label: "Shopify Development", to: "/services" },
  { label: "Ecommerce Development", to: "/services" },
  { label: "Store Redesign", to: "/services" },
  { label: "Ecommerce Migration", to: "/services" },
  { label: "SEO & CRO Optimization", to: "/services" },
  { label: "Custom AI Automation", to: "/services" },
];
const FOOTER_COMPANY_LINKS = [
  { label: "Work", to: "/work" },
  { label: "Contact", to: "/contact-us" },
  { label: "Privacy Policy", to: "/privacy-policy" },
  { label: "Refund Policy", to: "/refund-policy" },
  { label: "Terms of Service", to: "/terms-of-service" },
];

export function Footer() {
  return (
    <footer style={{ background: "#0A0F1E", color: "#8B93A7", padding: "64px 24px 28px", borderTop: "1px solid #23304A" }}>
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr 1fr", gap: 32, paddingBottom: 40, borderBottom: "1px solid #23304A" }} className="footer-grid">
          <div>
            <div style={{ marginBottom: 16 }}>
              <BrandLockup tagline height={50} />
            </div>
            <p style={{ fontSize: 13, lineHeight: 1.7, maxWidth: 260, color: "#8B93A7" }}>
              Market-ready Shopify and ecommerce stores built for search, conversion and growth.
            </p>
          </div>
          <div>
            <div style={{ color: "#F6F4EE", fontSize: 13, fontWeight: 600, marginBottom: 14 }}>Services</div>
            {FOOTER_SERVICE_LINKS.map((s) => (
              <Link key={s.label} to={s.to} className="nav-link" style={{ display: "block", fontSize: 13, marginBottom: 10, padding: 0 }}>{s.label}</Link>
            ))}
          </div>
          <div>
            <div style={{ color: "#F6F4EE", fontSize: 13, fontWeight: 600, marginBottom: 14 }}>Company</div>
            {FOOTER_COMPANY_LINKS.map((c) => (
              <Link key={c.label} to={c.to} className="nav-link" style={{ display: "block", fontSize: 13, marginBottom: 10, padding: 0 }}>{c.label}</Link>
            ))}
          </div>
          <div>
            <div style={{ color: "#F6F4EE", fontSize: 13, fontWeight: 600, marginBottom: 14 }}>Get in touch</div>
            <a href="mailto:sales@jhalatech.com" className="nav-link" style={{ display: "block", fontSize: 13, marginBottom: 8, padding: 0 }}>sales@jhalatech.com</a>
            <a href="tel:+916263629689" className="nav-link" style={{ display: "block", fontSize: 13, marginBottom: 8, padding: 0 }}>+91 6263629689</a>
            <p style={{ fontSize: 13, color: "#8B93A7", margin: "0 0 16px" }}>Indore, Madhya Pradesh, India</p>
            <Link to="/contact-us" className="cta-btn" style={{ display: "inline-block", textDecoration: "none", background: "#4CE0B3", color: "#0A0F1E", border: "none", padding: "9px 16px", borderRadius: 4, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
              Start a Project
            </Link>
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12, fontSize: 12.5, paddingTop: 24 }}>
          <span>© 2026 JhalaTech. All rights reserved.</span>
          <span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>Innovate. Integrate. Elevate.</span>
        </div>
      </div>
    </footer>
  );
}